import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { chatsApi } from "@/lib/humhub-api/chats";

export const useSearchChatEntries = (initialKeyword?: string, initialMessageIds?: string[]) => {
  const [keyword, setKeyword] = useState<string | undefined>(initialKeyword);
  const [messageIds, setMessageIds] = useState<string[] | undefined>(initialMessageIds);
  const queryClient = useQueryClient();

  // React Queryでチャット検索
  const {
    data: response,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ["searchChatEntries", keyword, messageIds],
    queryFn: () => {
      if (!keyword) {
        throw new Error("Search keyword is undefined");
      }
      return chatsApi.searchEntries({ keyword, messageIds });
    },
    enabled: !!keyword, // キーワードがある場合のみクエリ実行
    staleTime: 2 * 60 * 1000, // 検索結果は2分間キャッシュ
  });

  // 検索実行関数
  const searchChatEntries = useCallback(
    async (searchKeyword = initialKeyword, searchMessageIds = initialMessageIds) => {
      if (!searchKeyword) {
        return;
      }

      setKeyword(searchKeyword);
      setMessageIds(searchMessageIds);
      // setKeywordが完了すると、useQueryのqueryKeyが変わり自動で新しいクエリが実行される
    },
    [initialKeyword, initialMessageIds],
  );

  // 手動リフェッチ
  const refetchSearch = useCallback(() => {
    if (keyword) {
      refetch();
    }
  }, [keyword, refetch]);

  // 検索履歴のプリフェッチ（オプション）
  const prefetchSearch = useCallback(
    (searchKeyword: string, searchMessageIds?: string[]) => {
      queryClient.prefetchQuery({
        queryKey: ["searchChatEntries", searchKeyword, searchMessageIds],
        queryFn: () =>
          chatsApi.searchEntries({ keyword: searchKeyword, messageIds: searchMessageIds }),
        staleTime: 2 * 60 * 1000,
      });
    },
    [queryClient],
  );

  // 検索結果をクリア
  const clearSearch = useCallback(() => {
    setKeyword(undefined);
    setMessageIds(undefined);
  }, []);

  return {
    isLoading,
    error: queryError ? String(queryError) : null,
    searchChatEntries,
    response,
    refetchSearch, // 新しく追加：手動リフェッチ
    prefetchSearch, // 新しく追加：プリフェッチ
    clearSearch, // 新しく追加：検索結果クリア
    currentKeyword: keyword, // 新しく追加：現在の検索キーワード
  };
};
